import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  TextField,
  IconButton,
  Button,
  useTheme,
  Avatar,
  CircularProgress
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";
import { useUpdateEmployee } from "../../hooks/api/useEmployee";
import { useUploadProfileImage } from "../../hooks/api/useProfile";
import { Employee, UpdateEmployeeFormData } from "./types/employees";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  KivuI18nContext,
  useKivunovaTranslation,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";

const employeeSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  phone: Yup.string().required("Phone number is required"),
  profileImage: Yup.string().optional()
}) as Yup.ObjectSchema<UpdateEmployeeFormData>;

interface UpdateEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  employee: Employee;
  handleSuccess?: () => void;
}

const UpdateEmployeeModal: React.FC<UpdateEmployeeModalProps> = ({
  open,
  onClose,
  employee,
  handleSuccess
}) => {
  const updateEmployee = useUpdateEmployee();
  const uploadProfileImage = useUploadProfileImage();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const { setAlert } = useContext(AlertBannerContext);

  const [previewImage, setPreviewImage] = useState<string>(
    employee?.profileImage || ""
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<UpdateEmployeeFormData>({
    resolver: yupResolver(employeeSchema),
    mode: "onChange",
    defaultValues: {
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      phone: employee.phone,
      profileImage: employee.profileImage
    }
  });

  const firstName = watch("firstName");
  const lastName = watch("lastName");

  useEffect(() => {
    if (employee) {
      reset({
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        phone: employee.phone,
        profileImage: employee.profileImage
      });
      setPreviewImage(employee.profileImage || "");
      setSelectedFile(null);
    }
  }, [employee, reset]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const getInitials = () => {
    const first = firstName?.[0] || "";
    const last = lastName?.[0] || "";
    return `${first}${last}`.toUpperCase();
  };

  const isUploading = uploadProfileImage.isPending;
  const isUpdating = updateEmployee.isPending;
  const isLoading = isUploading || isUpdating;

  const onSubmit: SubmitHandler<UpdateEmployeeFormData> = async (data) => {
    if (isLoading) return;

    try {
      let imageUrl = data.profileImage;

      if (selectedFile) {
        try {
          const uploadResult =
            await uploadProfileImage.mutateAsync(selectedFile);
          if (uploadResult.uploadProfileImage?.code === API_RESPONSE_CODE.OK) {
            imageUrl = uploadResult.uploadProfileImage?.user?.profileImage;
          } else {
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: ALERT_BANNER_CODE.INTERNAL_SERVER_ERROR
            });
          }
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : ALERT_BANNER_CODE.INTERNAL_SERVER_ERROR;

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: message as ALERT_BANNER_CODE
          });
        }
      }

      await updateEmployee.mutateAsync(
        { userId: employee.id, updates: { ...data, profileImage: imageUrl } },
        {
          onSuccess: (response) => {
            const code = response.updateUser?.code;
            if (code === API_RESPONSE_CODE.OK) {
              onClose();
              handleSuccess?.();
            } else {
              setAlert({
                severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
                code: code as ALERT_BANNER_CODE
              });
            }
          },
          onError: (error: unknown) => {
            const message =
              error instanceof Error
                ? error.message
                : ALERT_BANNER_CODE.INTERNAL_SERVER_ERROR;

            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: message as ALERT_BANNER_CODE
            });
          }
        }
      );
    } catch {
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_SERVER_ERROR
      });
    }
  };

  const renderField = (
    name: keyof UpdateEmployeeFormData,
    label: string,
    placeholder?: string,
    type: "text" | "email" | "password" = "text"
  ) => (
    <Box sx={{ width: matchMD ? "90%" : "80%", margin: "16px auto 0 auto" }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label={label}
            placeholder={placeholder}
            type={type}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
        )}
      />
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: matchMD ? 1.5 : 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("update-employee", "Update Employee")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize={matchMD ? "small" : "medium"} />
          </IconButton>
        </Box>

        {/* Profile Image Upload */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: matchMD ? 2 : 3,
            mt: matchMD ? 1.5 : 2,
            position: "relative"
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Avatar
              src={previewImage}
              alt={employee?.firstName}
              sx={{
                width: matchMD ? 80 : 100,
                height: matchMD ? 80 : 100,
                fontSize: matchMD ? "1.5rem" : "2rem",
                bgcolor: BRAND_COLOR,
                border: `4px solid ${BRAND_COLOR}20`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
              }}
            >
              {!previewImage && getInitials()}
            </Avatar>

            {isUploading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%"
                }}
              >
                <CircularProgress
                  size={matchMD ? 24 : 30}
                  sx={{ color: "white" }}
                />
              </Box>
            )}

            <input
              accept="image/*"
              style={{ display: "none" }}
              id="employee-image-upload"
              type="file"
              onChange={handleImageChange}
              disabled={isLoading}
            />
            <label htmlFor="employee-image-upload">
              <IconButton
                component="span"
                disabled={isLoading}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  bgcolor: BRAND_COLOR,
                  color: "white",
                  width: matchMD ? 32 : 36,
                  height: matchMD ? 32 : 36,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  "&:hover": { bgcolor: BRAND_HOVER, transform: "scale(1.05)" },
                  transition: "all 0.2s ease-in-out"
                }}
              >
                <CameraAltIcon sx={{ fontSize: matchMD ? 16 : 18 }} />
              </IconButton>
            </label>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 1,
              textAlign: "center",
              px: matchMD ? 2 : 0
            }}
          >
            {firstName && lastName
              ? `${firstName} ${lastName}`
              : t(
                  "upload-photo-hint",
                  "Click the camera icon to upload a photo"
                )}
          </Typography>
        </Box>

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: matchMD ? 1.5 : 2,
            flex: 1,
            overflow: "auto",
            pb: matchMD ? 2 : 3
          }}
        >
          {renderField("firstName", t("first-name", "First Name"))}
          {renderField("lastName", t("last-name", "Last Name"))}
          {renderField(
            "email",
            t("email", "Email"),
            "example@kivunova.com",
            "email"
          )}
          {renderField("phone", t("phone", "Phone"), "+2507...")}

          <Box
            sx={{
              display: "flex",
              flexDirection: matchMD ? "column" : "row",
              gap: 2,
              mt: 4,
              width: matchMD ? "90%" : "80%",
              margin: "auto",
              flexShrink: 0
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              sx={{
                flex: 1,
                textTransform: "none",
                borderRadius: 2,
                py: matchMD ? 1 : 1.25,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("update-employee", "Update Employee")}
            </LoadingButton>
            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              sx={{
                flex: 1,
                textTransform: "none",
                borderRadius: 2,
                py: matchMD ? 1 : 1.25,
                backgroundColor: "white",
                color: BRAND_COLOR,
                border: `1px solid ${BRAND_COLOR}`,
                "&:hover": { backgroundColor: BRAND_HOVER, color: "white" }
              }}
            >
              {t("cancel", "Cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateEmployeeModal;
